import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Search, Download, ExternalLink } from "lucide-react";

const KIND_LABEL = {
  invoice: "Invoice",
  tournament_team: "Tournament",
  donation: "Donation",
};
const STATUS_STYLE = {
  paid: "bg-green-500/15 text-green-300 border-green-500/40",
  pending: "bg-yellow-500/15 text-yellow-200 border-yellow-500/40",
  failed: "bg-red-500/15 text-red-300 border-red-500/40",
  created: "bg-white/10 text-white/60 border-white/20",
};

function formatINR(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}
function formatDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminTransactions() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [refreshing, setRefreshing] = useState(null);

  useEffect(() => { load(); loadSummary(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (kind) params.append("kind", kind);
      if (q) params.append("q", q);
      const { data } = await api.get(`/admin/transactions?${params.toString()}`);
      setRows(data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const loadSummary = async () => {
    try { const { data } = await api.get("/admin/transactions/summary"); setSummary(data); } catch { /* summary is optional */ }
  };
  const refreshRow = async (id) => {
    setRefreshing(id);
    try {
      const { data } = await api.post(`/admin/razorpay/refresh-status/${id}`);
      toast.success(`Status: ${data.status || "updated"}`);
      await load(); await loadSummary();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRefreshing(null); }
  };

  const exportCsv = () => {
    const headers = ["Date", "Kind", "Amount", "Status", "Method", "Payer", "Phone", "Order ID", "Payment ID", "Description"];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      const cells = [
        formatDT(r.created_at),
        KIND_LABEL[r.kind] || r.kind,
        r.amount || 0,
        r.payment_status,
        r.method || "",
        r.payer_name || "",
        r.payer_phone || "",
        r.razorpay_order_id || "",
        r.razorpay_payment_id || "",
        (r.description || "").replace(/,/g, ";"),
      ];
      lines.push(cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const summaryCards = useMemo(() => {
    if (!summary) return null;
    const paid = summary.paid || { count: 0, amount: 0 };
    const pending = summary.pending || { count: 0, amount: 0 };
    const failed = summary.failed || { count: 0, amount: 0 };
    return { paid, pending, failed };
  }, [summary]);

  return (
    <div data-testid="admin-transactions">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Business · Online Payments</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Transactions.</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { load(); loadSummary(); }} data-testid="txn-refresh"
            className="border border-white/20 px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-white/6 flex items-center gap-2">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={exportCsv} data-testid="txn-export"
            className="border border-white/20 px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-white/6 flex items-center gap-2">
            <Download size={14} /> Export CSV
          </button>
          <a href="https://dashboard.razorpay.com/app/payments" target="_blank" rel="noreferrer"
            className="border border-brand-blue text-brand-blue px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-brand-blue/10 flex items-center gap-2">
            <ExternalLink size={14} /> Razorpay Dashboard
          </a>
        </div>
      </div>

      {/* Summary */}
      {summaryCards && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <SumCard label="Paid" count={summaryCards.paid.count} amount={summaryCards.paid.amount} tone="green" />
          <SumCard label="Pending" count={summaryCards.pending.count} amount={summaryCards.pending.amount} tone="yellow" />
          <SumCard label="Failed" count={summaryCards.failed.count} amount={summaryCards.failed.amount} tone="red" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-[#131A2B] border border-white/12 p-4 flex items-center gap-3 flex-wrap mb-6">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name, phone, order id, payment id"
            data-testid="txn-search"
            className="w-full bg-transparent border border-white/20 pl-9 pr-3 py-2 text-sm"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="txn-status-filter"
          className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm">
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="txn-kind-filter"
          className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm">
          <option value="">All kinds</option>
          <option value="invoice">Invoice</option>
          <option value="tournament_team">Tournament</option>
          <option value="donation">Donation</option>
        </select>
        <button onClick={load} data-testid="txn-apply-filters"
          className="bg-brand-blue text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:glow-blue">
          Apply
        </button>
      </div>

      {/* Table */}
      <div className="bg-[#131A2B] border border-white/12 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/12 text-[10px] font-mono uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Kind</th>
              <th className="text-left px-4 py-3">Payer</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Method</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Order / Payment ID</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-8 text-center text-white/40">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-white/40">No transactions yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`txn-row-${r.id}`} className="border-b border-white/6 hover:bg-white/4">
                <td className="px-4 py-3 text-white/70 text-xs">{formatDT(r.created_at)}</td>
                <td className="px-4 py-3"><span className="font-mono text-xs brand-blue uppercase">{KIND_LABEL[r.kind] || r.kind}</span></td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.payer_name || "—"}</div>
                  <div className="text-[11px] text-white/40">{r.payer_phone || ""}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono">{formatINR(r.amount)}</td>
                <td className="px-4 py-3 text-white/70 text-xs">{r.method || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${STATUS_STYLE[r.payment_status] || STATUS_STYLE.created}`}>
                    {r.payment_status || "—"}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-white/60">
                  <div className="truncate max-w-[220px]">{r.razorpay_order_id || "—"}</div>
                  {r.razorpay_payment_id && <div className="truncate max-w-[220px] text-white/40">{r.razorpay_payment_id}</div>}
                </td>
                <td className="px-4 py-3">
                  {r.payment_status !== "paid" && (
                    <button
                      onClick={() => refreshRow(r.id)}
                      disabled={refreshing === r.id}
                      data-testid={`txn-refresh-${r.id}`}
                      className="text-xs font-mono uppercase text-brand-blue hover:underline disabled:opacity-40"
                    >
                      {refreshing === r.id ? "…" : "Re-check"}
                    </button>
                  )}
                  {r.short_url && (
                    <a href={r.short_url} target="_blank" rel="noreferrer" className="text-xs font-mono uppercase brand-orange hover:underline ml-2">
                      Link
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SumCard({ label, count, amount, tone }) {
  const toneClass = tone === "green" ? "border-green-500/40" : tone === "yellow" ? "border-yellow-500/40" : "border-red-500/40";
  return (
    <div className={`bg-[#131A2B] border ${toneClass} p-5`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">{label}</div>
      <div className="font-display text-3xl font-black">{formatINR(amount)}</div>
      <div className="text-xs text-white/40 mt-1">{count} transaction{count !== 1 ? "s" : ""}</div>
    </div>
  );
}
